import { Grid, NumberInput, Skeleton, Stack } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';

import { LabelWithInfo } from '../../ui/LabelWithInfo.js';
import { FormSubsection } from '../../ui/FormSubsection.js';
import { DECIMAL_INPUT_PROPS } from '../../form/numberFieldPresets.js';
import { ValidatedNumberInput } from '../../form/validatedFields.js';
import { PreviousValueForField } from '../../form/PreviousValueLine.js';
import { RegisterFieldTab } from '../../form/RegisterFieldTab.js';
import { withUnit, unitSuffix } from '../../form/unitLabel.js';
import type {
  EnvironmentalCalculations,
  EnvironmentalDataFormData,
} from '../../../types/reports.js';

interface ProduktionSectionProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  productionCalc: EnvironmentalCalculations | null;
  disabled?: boolean;
  loading?: boolean;
}

// clinkerStockChange wird NIE im Frontend nachgerechnet und NIE in den Formularzustand
// zurueckgeschrieben -- der Server ist autoritativ (Muster WasserSection, waterConsumption).
// Aufbereitung identisch: gesetzt und nicht Leerstring -> parseFloat, sonst Leerstring.
function clinkerStockChangeValue(value: string | null | undefined): number | '' {
  return value != null && value !== '' ? parseFloat(value) : '';
}

export function ProduktionSection({
  form,
  productionCalc,
  disabled,
  loading,
}: ProduktionSectionProps) {
  if (loading) {
    return (
      <Grid>
        {Array.from({ length: 10 }, (_, i) => i).map((i) => (
          <Grid.Col span={{ base: 12, md: 6 }} key={i}>
            <Skeleton height={60} />
          </Grid.Col>
        ))}
      </Grid>
    );
  }

  return (
    <Stack>
      <FormSubsection
        title="Klinker- und Zementproduktion"
        description="Alle Mengenangaben in Tonnen (t)"
      >
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="clinkerProduction"
              kind="decimal"
              label="Klinkerproduktion"
              disabled={disabled}
              suffix=" t"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="cementProduction"
              kind="decimal"
              label="Zementproduktion"
              disabled={disabled}
              suffix=" t"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      <FormSubsection title="Klinkeranlieferung">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="clinkerReceiptDomestic"
              kind="decimal"
              label="Klinkeranlieferung Inland"
              disabled={disabled}
              suffix=" t"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="clinkerReceiptImport"
              kind="decimal"
              label="Klinkeranlieferung Import"
              disabled={disabled}
              suffix=" t"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      <FormSubsection title="Klinkerversand">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="clinkerShipmentDomestic"
              kind="decimal"
              label="Klinkerversand Inland"
              disabled={disabled}
              suffix=" t"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="clinkerShipmentExport"
              kind="decimal"
              label="Klinkerversand Export"
              disabled={disabled}
              suffix=" t"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      <FormSubsection title="Klinkerlagerbestand">
        <Grid>
          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedNumberInput
              form={form}
              field="clinkerStockJan01"
              kind="decimal"
              label="Bestand zum 1. Januar"
              disabled={disabled}
              unit="t"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <ValidatedNumberInput
              form={form}
              field="clinkerStockDec31"
              kind="decimal"
              label="Bestand zum 31. Dezember"
              disabled={disabled}
              unit="t"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            {/* Wert kommt ausschliesslich aus productionCalc (Server-Antwort), NIE aus einer
                eigenen Subtraktion ueber form.getValues() -- siehe clinkerStockChangeValue oben. */}
            <NumberInput
              label={withUnit(
                <LabelWithInfo
                  label="Veränderung"
                  info="Automatisch berechnet: Bestand zum 31. Dezember minus Bestand zum 1. Januar. Kann negativ sein. Schreibgeschützt."
                />,
                't',
              )}
              value={clinkerStockChangeValue(productionCalc?.clinkerStockChange)}
              readOnly
              disabled
              suffix={unitSuffix('t')}
              {...DECIMAL_INPUT_PROPS}
            />
            <RegisterFieldTab field="clinkerStockChange" label="Klinkerbestand: Veränderung" />
            <PreviousValueForField field="clinkerStockChange" />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      <FormSubsection title="Klinkergehalt">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="averageClinkerContent"
              kind="decimal"
              fieldLabel="Durchschnittlicher Klinkergehalt im Zement"
              label={
                <LabelWithInfo
                  label="Durchschnittlicher Klinkergehalt im Zement"
                  info="Gewichteter Durchschnitt aller produzierten Zemente"
                />
              }
              disabled={disabled}
              unit="%"
              max={100}
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>
        </Grid>
      </FormSubsection>

      <FormSubsection title="Ausgeschleuste Staubmengen">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="dustBypass"
              kind="decimal"
              label="Bypass"
              disabled={disabled}
              suffix=" t"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <ValidatedNumberInput
              form={form}
              field="dustExhaustFilter"
              kind="decimal"
              label="Abgasfilter"
              disabled={disabled}
              suffix=" t"
              {...DECIMAL_INPUT_PROPS}
            />
          </Grid.Col>
        </Grid>
      </FormSubsection>
    </Stack>
  );
}
